import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DeepJsonEditor } from "./DeepJsonEditor";
import { ImageUploadPanel } from "./ImageUploadPanel";
import { UniversalPageRenderer } from "@/components/sacred-pages/UniversalPageRenderer";
import {
  fetchPageForEditor,
  savePage,
  submitPage,
  publishPage,
  pageAssetUrl,
} from "@/lib/sacred-pages/sacred-pages-api";
import { getDefaultSacredPage } from "@/lib/sacred-pages/defaults";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";

const DRAFT_KEY = (slug) => `sacred_page_draft_${slug}`;

export function PageEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { canEditPages, canPublishPages, isAdmin, editorUser } = useSacredAdmin();

  const [doc, setDoc] = useState(null);
  const [jsonError, setJsonError] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const fallback = getDefaultSacredPage(slug);
    try {
      const remote = await fetchPageForEditor(slug);
      const merged = {
        ...fallback,
        ...(remote.published || {}),
        ...(remote.draft || {}),
        slug,
        metadata: {
          ...fallback.metadata,
          ...(remote.published?.metadata || remote.draft?.metadata || {}),
          hero_image: remote.asset_url ? pageAssetUrl(slug) : fallback.metadata?.hero_image,
        },
      };
      setDoc(merged);
    } catch {
      const raw = localStorage.getItem(DRAFT_KEY(slug));
      setDoc(raw ? JSON.parse(raw) : { ...fallback, slug });
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => {
    if (!canEditPages) {
      navigate("/admin/pages");
      return;
    }
    load();
  }, [canEditPages, load, navigate]);

  useEffect(() => {
    if (!doc) return;
    const t = setTimeout(() => localStorage.setItem(DRAFT_KEY(slug), JSON.stringify(doc)), 800);
    return () => clearTimeout(t);
  }, [doc, slug]);

  if (!canEditPages) return null;
  if (loading || !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const needsApproval = !canPublishPages;

  const onSaveDraft = async () => {
    setSaving(true);
    try {
      await savePage(slug, { page: doc, draft_only: true });
      setStatus("Draft saved.");
    } catch {
      setStatus("Draft saved locally.");
    }
    setSaving(false);
  };

  const onPublish = async () => {
    setSaving(true);
    try {
      await publishPage(slug, doc);
      setStatus("Published.");
    } catch (e) {
      try {
        await savePage(slug, { page: doc, publish: true });
        setStatus("Published.");
      } catch (e2) {
        setStatus(e2?.response?.data?.detail || "Publish failed");
      }
    }
    setSaving(false);
  };

  const onSubmit = async () => {
    setSaving(true);
    try {
      await submitPage(slug, doc);
      setStatus("Submitted for approval.");
    } catch {
      setStatus("Submit failed");
    }
    setSaving(false);
  };

  const pageJson = {
    slug: doc.slug,
    title: doc.title,
    sections: doc.sections,
    metadata: doc.metadata,
  };

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="page-editor">
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" size="sm" className="mb-4" asChild>
          <Link to="/admin/pages">
            <ArrowLeft className="w-4 h-4 mr-1" /> All pages
          </Link>
        </Button>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Edit page: {slug}</CardTitle>
            <p className="text-sm text-muted-foreground">
              JSON document (sections + metadata). Role: {editorUser?.role}.
              {needsApproval ? " Submit for admin approval." : " You can publish directly."}
            </p>
            {status && <p className="text-sm text-primary">{status}</p>}
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} />
            </div>
            <div>
              <Label>Slug</Label>
              <Input value={doc.slug} disabled className="bg-muted" />
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Page JSON</CardTitle>
            </CardHeader>
            <CardContent>
              <DeepJsonEditor
                value={pageJson}
                onChange={(parsed) => {
                  setDoc({
                    slug: parsed.slug || slug,
                    title: parsed.title ?? doc.title,
                    sections: parsed.sections ?? [],
                    metadata: parsed.metadata ?? {},
                  });
                }}
                error={jsonError}
                onError={setJsonError}
              />
              <p className="text-xs text-muted-foreground mt-2">
                sections[]: id, type (text|html|markdown|json|image), display (inline|block). Use inline{" "}
                <code>labels</code> JSON for page UI strings; <code>features</code> JSON for card grids.
              </p>
            </CardContent>
          </Card>
          <div className="space-y-6">
            <ImageUploadPanel
              mode="page"
              topicType="page"
              id={slug}
              imageUrl={doc.metadata?.hero_image}
              onUploaded={() =>
                setDoc((d) => ({
                  ...d,
                  metadata: { ...d.metadata, hero_image: pageAssetUrl(slug) },
                }))
              }
            />
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <UniversalPageRenderer page={doc} />
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-3 sticky bottom-4 bg-background/90 p-4 rounded-lg border shadow-lg">
          <Button variant="outline" onClick={onSaveDraft} disabled={saving}>
            <Save className="w-4 h-4 mr-2" /> Save draft
          </Button>
          {needsApproval ? (
            <Button onClick={onSubmit} disabled={saving || !!jsonError}>
              <Send className="w-4 h-4 mr-2" /> Submit for approval
            </Button>
          ) : (
            <Button onClick={onPublish} disabled={saving || !!jsonError}>
              Publish
            </Button>
          )}
          {isAdmin && needsApproval && (
            <Button variant="secondary" onClick={onPublish} disabled={saving}>
              Admin publish
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
